import React, { useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { PENDING, IN_PROGRESS, DEFERRED, IDLE } from "const"
import { MtTask } from "server/models"
import { deepCopy } from "client/utils"
import {
    TaskObjectProps,
    StoreType,
    TaskParticipantList,
    TaskObserverList,

    GetTaskAccess,
    DeleteTaskParticipant,
    DeleteTaskObserver,

    setTaskParticipantList,
    setTaskObserverList,
    addModal,
} from "client/store"
import {
    FormGroup,
    Label,
    LabelButton,
    DateFormatter,
    Delimiter,
    UserIcon,
    TagEditor,
    DeleteButton,
    ExclamationIcon,
} from "client/components"
import {
    TaskStateColumn,
    TaskStateHeader,
    TaskStateBody,
    TaskStatus,
    TaskStatusSince,
    TaskOverdueWrapper,
    UserRow,
} from "./styled"

import DelegateUsers from "./delegateUsers"
import AddParticipants from "./participants"
import AddObservers from "./observers"

const sinceArray = [PENDING, IN_PROGRESS, DEFERRED]

const State = ({
    task,
}: TaskObjectProps) => {
    const taskParticipantList = useSelector((state: StoreType) => state.taskParticipant.taskParticipantList)
    const taskObserverList = useSelector((state: StoreType) => state.taskObserver.taskObserverList)

    const userList = useSelector((state: StoreType) => state.user.userList)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const selectedTags: number[] = []
    if (task.tags) {
        task.tags.forEach((row) => {
            selectedTags.push(row.tagId)
        })
    }

    const { allowedToEdit } = GetTaskAccess(task, taskParticipantList, taskObserverList, userData)

    const nowTime = new Date().getTime()
    const deadlineTime = task.deadline ? new Date(task.deadline).getTime() : null
    const overdue = task.deadline && (deadlineTime - nowTime) < 0

    return (
        <TaskStateColumn>
            <TaskStateHeader>
                <TaskStatus>{lang[task.status]}</TaskStatus>
                {
                    sinceArray.includes(task.status)
                    && <TaskStatusSince>{lang.since}</TaskStatusSince>
                }
                <DateFormatter date={task.statusChanged} />
            </TaskStateHeader>
            <TaskStateBody>
                <FormGroup>
                    <Label>{lang.deadline}</Label>
                    <DateFormatter date={task.deadline} />
                </FormGroup>
                <Delimiter size="xs" />

                {
                    overdue && (
                        <>
                            <TaskOverdueWrapper>
                                <ExclamationIcon />
                                { lang.taskOverdue }
                            </TaskOverdueWrapper>
                            <Delimiter size="xs" />
                        </>
                    )
                }

                <FormGroup>
                    <Label>{lang.created}</Label>
                    <DateFormatter date={task.createdAt} />
                </FormGroup>
                <Delimiter size="sm" />

                <FormGroup>
                    <Label>{lang.creator}</Label>
                    <UserIcon
                        user={userList[task.creator]}
                        size="sm"
                        projectId={task.projectId}
                        showProjectStatus
                    />
                </FormGroup>
                <Delimiter size="sm" />

                <FormGroup>
                    <Label>
                        {lang.responsible}
                        {
                            allowedToEdit && (
                                <LabelButton onClick={() => dispatch(addModal({
                                    key: "changeResponsible",
                                    content: <DelegateUsers title={lang.changeResponsible} task={task} />,
                                }))}
                                >
                                    {lang.change}
                                </LabelButton>
                            )
                        }
                    </Label>
                    <UserIcon
                        user={userList[task.responsible]}
                        size="sm"
                        projectId={task.projectId}
                        showProjectStatus
                    />
                </FormGroup>
                <Delimiter size="sm" />

                <Participants allowedToEdit={allowedToEdit} task={task} />
                <Delimiter size="sm" />

                <Observers allowedToEdit={allowedToEdit} task={task} />
                <Delimiter size="sm" />

                <FormGroup>
                    <Label>{lang.tags}</Label>
                    <TagEditor
                        selectedIds={selectedTags}
                        readOnly
                    />
                </FormGroup>
            </TaskStateBody>
        </TaskStateColumn>
    )
}
export default State


interface TaskUsersProps {
    allowedToEdit: boolean
    task: MtTask
}

const Participants = ({
    allowedToEdit,
    task,
}: TaskUsersProps) => {
    const taskParticipantList = useSelector((state: StoreType) => state.taskParticipant.taskParticipantList)

    const userList = useSelector((state: StoreType) => state.user.userList)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [removingParticipant, setRemovingParticipant] = useState<number>(null)

    const deleteParticipant = (userId: number) => {
        if (removingParticipant !== null) return

        setRemovingParticipant(userId)
        DeleteTaskParticipant(userId, task.id).then((res) => {
            setRemovingParticipant(null)
            if (res === 1) {
                const newParticipants = taskParticipantList && taskParticipantList[task.id] ? taskParticipantList[task.id] : []
                const newParticipantsFiltered = newParticipants.filter((id) => (id !== userId))

                const taskParticipantListCopy: TaskParticipantList = taskParticipantList ? deepCopy(taskParticipantList) : {}
                taskParticipantListCopy[task.id] = newParticipantsFiltered
                dispatch(setTaskParticipantList(taskParticipantListCopy))
            }
        })
    }

    return (
        <FormGroup>
            <Label>
                {lang.participants}
                {
                    allowedToEdit && (
                        <LabelButton onClick={() => dispatch(addModal({
                            key: "addParticipants",
                            content: <AddParticipants task={task} />,
                        }))}
                        >
                            {lang.add}
                        </LabelButton>
                    )
                }
            </Label>
            {
                taskParticipantList
                && taskParticipantList[task.id]
                && taskParticipantList[task.id].map((userId) => (
                    <UserRow key={userId}>
                        <UserIcon
                            user={userList[userId]}
                            size="sm"
                            projectId={task.projectId}
                            showProjectStatus
                        />
                        {
                            allowedToEdit
                            && (
                                <DeleteButton
                                    status={removingParticipant === userId ? PENDING : IDLE}
                                    onClick={() => deleteParticipant(userId)}
                                />
                            )
                        }
                    </UserRow>
                ))
            }
        </FormGroup>
    )
}


export const Observers = ({
    allowedToEdit,
    task,
}: TaskUsersProps) => {
    const taskObserverList = useSelector((state: StoreType) => state.taskObserver.taskObserverList)

    const userList = useSelector((state: StoreType) => state.user.userList)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [removingObserver, setRemovingObserver] = useState<number>(null)

    const deleteObserver = (userId: number) => {
        if (removingObserver !== null) return

        setRemovingObserver(userId)
        DeleteTaskObserver(userId, task.id).then((res) => {
            setRemovingObserver(null)
            if (res === 1) {
                const newObservers = taskObserverList && taskObserverList[task.id] ? taskObserverList[task.id] : []
                const newObserversFiltered = newObservers.filter((id) => (id !== userId))

                const taskObserverListCopy: TaskObserverList = taskObserverList ? deepCopy(taskObserverList) : {}
                taskObserverListCopy[task.id] = newObserversFiltered
                dispatch(setTaskObserverList(taskObserverListCopy))
            }
        })
    }

    return (
        <FormGroup>
            <Label>
                {lang.observers}
                {
                    allowedToEdit && (
                        <LabelButton onClick={() => dispatch(addModal({
                            key: "addObservers",
                            content: <AddObservers task={task} />,
                        }))}
                        >
                            {lang.add}
                        </LabelButton>
                    )
                }
            </Label>
            {
                taskObserverList
                && taskObserverList[task.id]
                && taskObserverList[task.id].map((userId) => (
                    <UserRow key={userId}>
                        <UserIcon
                            user={userList[userId]}
                            size="sm"
                            projectId={task.projectId}
                            showProjectStatus
                        />
                        {
                            allowedToEdit
                            && (
                                <DeleteButton
                                    status={removingObserver === userId ? PENDING : IDLE}
                                    onClick={() => deleteObserver(userId)}
                                />
                            )
                        }
                    </UserRow>
                ))
            }
        </FormGroup>
    )
}
